import * as THREE from 'three'

// ============================================================
// LA MANO CHE TIENE IL PRODOTTO.
//
// Chiesto: «vorrei come se prendesse l'oggetto e lo trattenesse in mano [...]
// e che questa animazione segua la posizione del modello che viene preso, in
// modo che la mano si trovi sempre dietro il prodotto ovunque si guardi».
// Quindi NON e' una clip: una clip e' una posa fissa, e il prodotto invece si
// muove — ruota, cambia distanza, e la vetrina lo tiene davanti alla camera
// dovunque si guardi. Serve una CINEMATICA INVERSA risolta a ogni fotogramma:
// si dice al braccio dove finisce la mano e lui ci si piega dietro.
//
// IL BRACCIO E' LUNGO 0.881 (omero 0.384 + avambraccio 0.497, misurati in
// gioco): e' questo numero a decidere dove puo' stare il prodotto. Con la
// vetrina a 1.6 dall'occhio il bersaglio distava 1.834 dalla spalla, cioe' piu'
// del doppio della portata: la mano non poteva arrivarci in nessun modo. Per
// questo il prodotto in mano ora sta molto piu' vicino (config "Vetrina
// prodotto"), rimpicciolito in proporzione perche' sullo schermo resti grande
// come prima.
//
// Tutto e' espresso in MONDO e riportato in locale osso per osso: cosi' non
// serve sapere come l'esportatore ha orientato gli assi delle ossa, che e'
// esattamente il genere di cosa che fa perdere un pomeriggio.
// ============================================================

// Parametri tarabili (cursori Leva "Presa", scrivibili da console in ?debug)
export const cfgPresa = {
  attiva: true,
  // QUANTO SI RIMPICCIOLISCE LA MANO. Segnalato tre volte che e' troppo grande,
  // e l'ultima con mano libera («fai tutto quello che senti necessario»).
  // Non era un effetto ottico: e' il MODELLO ad averla sovradimensionata.
  // Misurati i tratti in gioco, omero 0.384, avambraccio 0.497, mano
  // (polso-punta del medio) 0.486, cioe' rapporti 1 : 1.29 : 1.27; in un
  // braccio umano valgono 1 : 0.78 : 0.58. Anche prendendo come riferimento
  // l'altezza del personaggio (3.63 dalla pianta alla cima della testa) la
  // mano dovrebbe misurare 0.39 e non 0.486.
  // Si scala l'osso del POLSO: la pelle segue le ossa, quindi tutta la mano si
  // rimpicciolisce attorno all'articolazione e l'avambraccio non si tocca.
  // L'orologio, che e' figlio dello stesso osso, viene contro-scalato perche'
  // resti identico (sta sull'avambraccio, che non cambia).
  scalaMano: 0.78,
  // DA CHE PARTE GUARDA IL PALMO: 0 = verso chi guarda (il prodotto gli sta
  // davanti), 1 = in SU, col prodotto appoggiato dentro la mano. Chiesto:
  // «voglio che nel palmo della mano ci sia il prodotto con origine ai suoi
  // piedi». A palmo in su la mano si vede anche DI TAGLIO invece che di
  // piatto, quindi occupa molto meno schermo — ed e' l'altra meta' della
  // risposta a «la mano e' troppo grande».
  // Tutto il resto del codice non sa da che parte guardi il palmo: lavora
  // sulla NORMALE DEL PALMO qualunque essa sia (vedi `nMira`), cosi' la regola
  // «nessun pezzo di mano deve sporgere oltre il prodotto» continua a valere
  // identica — solo che la sporgenza si misura in su invece che in avanti, e
  // il confronto e' con l'ALTEZZA del prodotto invece che col suo spessore.
  palmoSu: 1,
  // DOVE, LUNGO LA MANO, SI APPOGGIA IL PRODOTTO. 1 = al centro del palmo
  // (meta' strada fra il polso e la base del medio); alzandolo il punto
  // d'appoggio scorre verso le dita e il prodotto sale. Chiesto: «il prodotto
  // deve essere centrato nella mano (piu' su)» — col piede al centro del palmo
  // il prodotto occupava la meta' bassa della mano e le dita gli svettavano
  // sopra.
  ancora: 1.6,
  // ...e di lato e sopra/sotto il piano del palmo, per finire di posizionare il
  // prodotto nella mano dai cursori. Sono nel frame della MANO, non della
  // camera: restano validi comunque si giri il polso.
  appoggioLato: 0.01,      // + verso il pollice
  appoggioSopra: 0.03,     // + fuori dal palmo
  // QUANTO E' GRANDE L'OROLOGIO, in proporzione al modello originale. Con la
  // mano rimpicciolita l'orologio, che le sta attaccato, va rimpicciolito
  // insieme o sembra enorme — segnalato. A parita' con `scalaMano` mantiene la
  // proporzione con cui e' stato modellato; alzandolo cresce rispetto alla mano.
  scalaOrologio: 0.8,
  // di quanto la mano sta DIETRO al prodotto, lungo la normale del palmo
  // ritocco lungo la linea di vista: 0 perche' ormai l'ancora e' il palmo, e
  // qualunque valore diverso da zero verrebbe letto come "la mano non ci
  // arriva" dal correttore in ProductShowcase e farebbe scivolare il prodotto
  dietro: 0.07,
  // e di quanto piu' in basso: si tiene un oggetto un po' sotto il suo centro
  // LA MANO IMPUGNA DA SOTTO: e' quello che permette al prodotto di stare piu'
  // lontano restando alto in inquadratura, perche' la mano scende verso la
  // spalla invece di allontanarsene. Ed e' anche come si tiene davvero un
  // oggetto che si sta guardando.
  giu: 0.09,
  // dove va il gomito: in basso e all'indietro (vettore polo, frame del corpo)
  poloGiu: 2,
  poloDietro: 1.15,
  // QUANTO IL GOMITO PUO' CHIUDERSI (angolo interno, gradi). Segnalato:
  // «se guardo troppo in basso si intreccia tutto il braccio». Guardando in
  // basso il prodotto scende verso il corpo e il bersaglio viene a pochi
  // centimetri dalla spalla: MISURATO a 60 gradi sotto l'orizzonte, spalla-polso
  // 0.113, cioe' ESATTAMENTE il minimo geometrico |L1-L2| — il braccio piegato
  // in due, l'avambraccio ripiegato sull'omero. Un gomito vero si ferma molto
  // prima. Sotto questo angolo la mano non segue piu' il prodotto, e il
  // prodotto si allontana di conseguenza (lo stesso correttore che lo avvicina
  // quando il braccio non ci arriva, al contrario).
  gomitoMin: 82,
  // curvatura delle dita (gradi): base, media, punta
  // QUANTA TORSIONE PRENDE L'AVAMBRACCIO, e quanta ne resta al polso. A 1 la
  // prende tutta e la pelle si annoda al gomito (vedi la nota accanto al
  // calcolo); a 0 la prende tutta il polso e l'orologio gira con la mano, che
  // era la segnalazione di prima. Il tetto e' in gradi.
  torsioneAvambraccio: 0.55,
  torsioneMax: 75,
  // quanto il polso si piega rispetto all'avambraccio (gradi)
  piegaPolso: 7,
  // LA CLAVICOLA ENTRA NELLA CATENA. Il braccio da solo arriva a 0.881 dalla
  // spalla, e la spalla sta 0.67 sotto l'occhio: e' questo a impedire di
  // allontanare il prodotto (segnalato tre volte, «ancora troppo vicino»). Ma
  // una spalla vera non sta ferma: quando ci si allunga la clavicola ruota e
  // porta avanti tutto il braccio. Qui la si ruota di una FRAZIONE dell'angolo
  // che servirebbe a puntare il bersaglio, con un tetto in gradi perche' non
  // diventi una spallata.
  clavicola: 0.5,
  clavicolaMax: 26,
  // LE DITA SI CHIUDONO SOLO QUANTO L'OGGETTO PERMETTE. Questi sono i gradi a
  // mano PIENA, quelli della foto di riferimento; su un oggetto sottile si
  // usano in proporzione minore (vedi `fattoreDita` piu' sotto).
  dita1: 7,
  dita2: 36,
  dita3: 41,
  pollice1: 8,
  pollice2: 31,
  // quanto in fretta le dita si adattano allo spessore dell'oggetto (1/s)
  adattaDita: 10,
  // quanto la presa entra ed esce (secondi)
  tempo: 0.18,
}

// In ?debug si tara da console: window.__presa.dietro = 0.15, ecc.
if (typeof window !== 'undefined' && /[?#]debug/.test(window.location.search + window.location.hash)) {
  window.__presa = cfgPresa
}

const SENZA_ROTAZIONE = new THREE.Quaternion()
const SU = new THREE.Vector3(0, 1, 0)
// Quando il palmo non guarda verso chi guarda, le dita possono chiudersi quanto
// vogliono: un metro e' "nessun limite" per una mano lunga 38 centimetri.
const SENZA_LIMITE = 1

// Le punte delle dita non devono mai venire PIU' AVANTI della faccia anteriore
// del prodotto: appena succede, il prodotto sparisce dietro la mano. Questo e'
// il margine che si lascia fra le due (metri): zero vuol dire "a filo".
const MARGINE_DITA = 0

const tmp = {
  // ingombro del prodotto NEL FRAME DELLA CAMERA (vedi scatolaInCamera)
  scatola: new THREE.Box3(),
  piedi: new THREE.Vector3(),
  voluto: new THREE.Vector3(),
  spigolo: new THREE.Vector3(),
  // la normale del palmo VOLUTA: serve all'inizio (per decidere di quanto
  // arretrare e quanto possono chiudersi le dita) e di nuovo alla fine, per
  // orientare il polso. Scratch tutto suo perche' in mezzo tmp.n fa altro.
  nMira: new THREE.Vector3(),
  scarto: new THREE.Vector3(),
  palmo3: new THREE.Vector3(),
  a: new THREE.Vector3(),
  b: new THREE.Vector3(),
  c: new THREE.Vector3(),
  dir: new THREE.Vector3(),
  polo: new THREE.Vector3(),
  n: new THREE.Vector3(),
  su: new THREE.Vector3(),
  destra: new THREE.Vector3(),
  avanti: new THREE.Vector3(),
  bersaglio: new THREE.Vector3(),
  q1: new THREE.Quaternion(),
  q2: new THREE.Quaternion(),
  qPar: new THREE.Quaternion(),
  qOsso: new THREE.Quaternion(),
  // scratch SEPARATO per la torsione: ruotaInMondo usa qPar e qOsso al suo
  // interno, quindi passargli uno dei due significa vederselo sovrascrivere
  // sotto i piedi. E' successo: l'avambraccio veniva ruotato con un
  // quaternione spazzatura e la mano finiva a mezzo metro dal prodotto.
  qTors: new THREE.Quaternion(),
  m1: new THREE.Matrix4(),
  m2: new THREE.Matrix4(),
  mCam: new THREE.Matrix4(),
}

// ------------------------------------------------------------
// INGOMBRO DEL PRODOTTO NEL FRAME DELLA CAMERA, non in mondo.
// Prima si usava Box3.setFromObject, che da' l'ingombro allineato agli ASSI
// DEL MONDO: su un prodotto girato verso la camera quella scatola e' gonfia, e
// lo spessore che se ne ricava non e' quello vero. MISURATO sulla maglietta: il
// mondo dice 0.359 di profondita', la camera 0.099 — un fattore 3.6, e da quel
// numero dipende quanto le dita possono chiudersi. Nel frame della camera
// invece la scatola e' stretta al prodotto, perche' la vetrina lo tiene sempre
// girato verso di noi: -Z e' lo sguardo, quindi max.z e' la faccia ANTERIORE e
// min.y sono i piedi COSI' COME SI VEDONO (che guardando in basso non sono i
// piedi secondo il mondo).
// Si saltano le mesh invisibili, sottoalbero compreso: l'anello sconto e gli
// altri pezzi spenti non devono allargare l'ingombro.
// ------------------------------------------------------------
function inCamera(o, fuori) {
  if (o.visible === false) return
  if (o.isMesh && o.geometry) {
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox()
    const bb = o.geometry.boundingBox
    if (bb) {
      tmp.m1.multiplyMatrices(tmp.mCam, o.matrixWorld)
      for (let i = 0; i < 8; i++) {
        // scratch TUTTO SUO: quando questa funzione gira, tmp.a tiene la
        // posizione dell'occhio, che serve subito dopo. E' la stessa trappola
        // che aveva gia' fatto finire la mano a mezzo metro dal prodotto.
        fuori.expandByPoint(tmp.spigolo.set(
          i & 1 ? bb.max.x : bb.min.x,
          i & 2 ? bb.max.y : bb.min.y,
          i & 4 ? bb.max.z : bb.min.z,
        ).applyMatrix4(tmp.m1))
      }
    }
  }
  for (const f of o.children) inCamera(f, fuori)
}

function scatolaInCamera(oggetto, camera, fuori) {
  oggetto.updateWorldMatrix(true, true)
  // matrixWorldInverse la riscrive il renderer al momento del disegno, cioe'
  // DOPO di noi: qui ce la si calcola, cosi' non si legge quella del fotogramma
  // prima.
  tmp.mCam.copy(camera.matrixWorld).invert()
  fuori.makeEmpty()
  inCamera(oggetto, fuori)
  return !fuori.isEmpty()
}

// Prende dallo scheletro le ossa che servono e misura le lunghezze a riposo.
// `lato` e' 'L' o 'R'. Torna null se il rig non e' quello previsto.
export function preparaBraccio(radice, lato = 'L') {
  const trova = (n) => radice.getObjectByName(n)
  const clavicola = trova(`Shoulder${lato}`)
  const spalla = trova(`UpperArm${lato}`)
  const gomito = trova(`LowerArm${lato}`)
  const polso = trova(`Wrist${lato}`)
  if (!spalla || !gomito || !polso) return null

  // le dita, con i loro assi di piega ricavati dalla posa di riposo
  const dita = []
  // e le PUNTE, che servono a sapere quanto la mano sporge in avanti: sono
  // l'ultimo osso di ogni dito (Index4L e compagni, che non si piegano e quindi
  // non stanno nell'elenco qui sopra).
  const punte = []
  for (const nome of ['Index', 'Middle', 'Ring', 'Pinky', 'Thumb']) {
    const punta = trova(`${nome}4${lato}`) || trova(`${nome}3${lato}`)
    if (punta) punte.push(punta)
  }
  for (const nome of ['Index', 'Middle', 'Ring', 'Pinky', 'Thumb']) {
    for (let g = 1; g <= 3; g++) {
      const osso = trova(`${nome}${g}${lato}`)
      if (!osso) continue
      const figlio = osso.children.find((f) => f.isBone)
      // la direzione DELL'OSSO e' l'offset del figlio a riposo; dove non c'e'
      // figlio (l'ultima falange) si riusa quella del pezzo precedente
      const lungo = figlio
        ? figlio.position.clone().normalize()
        : dita[dita.length - 1]?.lungo.clone() || new THREE.Vector3(0, 1, 0)
      // SI SEGNA LA POSA DI RIPOSO. Senza, la curvatura si moltiplicherebbe a
      // ogni fotogramma e le dita girerebbero all'infinito — ed e' successo:
      // dopo l'alleggerimento del GLB i canali che nessuna clip anima sono
      // stati tolti, quindi il mixer NON riscrive piu' quelle ossa e quello
      // che ci scriviamo sopra resta li' e si somma. (Middle2L, che Walk anima
      // ancora, stava fermo; Index1L, che non anima nessuno, drittava.)
      dita.push({ osso, lungo, nome, grado: g, riposo: osso.quaternion.clone() })
    }
  }

  // ASSI DELLA MANO, MISURATI SULLA MESH E NON DEDOTTI DALLE OSSA.
  // Il primo tentativo ricavava la normale del palmo da un prodotto vettoriale
  // fra l'asse delle dita e la direzione del pollice. Sbagliato: il pollice
  // sta FUORI dal piano del palmo, quindi quel prodotto dava un asse DENTRO
  // quel piano e la mano veniva mostrata DI TAGLIO — segnalato («si deve
  // vedere il palmo dietro il prodotto»), e misurato: la presunta normale
  // guardava l'occhio a -0.14 invece che a +1.
  // COME SI E' TROVATA QUELLA GIUSTA: una mano e' un oggetto PIATTO, quindi la
  // normale del palmo e' semplicemente la direzione su cui e' piu' SOTTILE. Si
  // prendono i vertici con peso sulle ossa di polso e dita (getVertexPosition
  // li da' gia' deformati), si provano 600 direzioni e si tiene quella di
  // spessore minimo. Risultato: spessore 0.135 contro 0.52 negli altri due
  // assi, e la direzione e' l'asse Z LOCALE del polso, allineata al 95%.
  // IL VERSO lo danno il pollice, che sta dalla parte del palmo (scostamento
  // -0.06/-0.08 su Z), e le punte delle dita curvate (-0.02): il palmo guarda
  // verso -Z.
  // Se un giorno si cambia il modello del personaggio, questa misura va
  // rifatta: la ricetta e' tutta qui sopra.
  const medio = trova(`Middle1${lato}`)
  const palmo = new THREE.Vector3(0, 0, -1)
  const lungoMano = (medio ? medio.position.clone() : new THREE.Vector3(0, 1, 0))
    .projectOnPlane(palmo).normalize()
  // terna DESTRORSA [versoPollice, lungoMano, palmo]: palmo = versoPollice x
  // lungoMano, quindi versoPollice = lungoMano x palmo. Con una terna mancina
  // setFromRotationMatrix (che presuppone una rotazione pura) da' un
  // quaternione sbagliato — gia' successo, palmo a -0.64.
  const versoPollice = new THREE.Vector3().crossVectors(lungoMano, palmo).normalize()

  // CENTRO DEL PALMO nel frame del polso: sta a meta' strada fra il polso e la
  // base del dito medio. E' preso sulle ossa NON scalate, quindi chi lo usa
  // deve moltiplicarlo per `scalaMano` prima di portarlo in mondo. E' li' che va appoggiato il prodotto (chiesto: «vorrei
  // che i piedi del modello fossero al centro del palmo»).
  const centroPalmo = medio ? medio.position.clone().multiplyScalar(0.5) : new THREE.Vector3()

  return {
    lato,
    centroPalmo,
    clavicola,
    // POSA A RIPOSO DI CLAVICOLA E POLSO. Nessuna clip le anima — MISURATO,
    // 0.01 gradi al secondo contro gli 0.9 dell'omero — quindi il mixer non le
    // riscrive e quello che ci scriviamo sopra RESTA. Senza ripartire ogni
    // volta da qui, la rotazione della clavicola per giunta si ACCUMULA
    // fotogramma per fotogramma (ruotaInMondo e' incrementale). E' esattamente
    // la trappola gia' presa con le dita, su due ossa aggiunte dopo: segnalato
    // come «quando rilascio il prodotto la mano e' tutta strana», e misurato —
    // due secondi e mezzo di presa lasciavano la clavicola a 154 gradi dalla
    // posa di idle e il polso a 122, mentre omero e avambraccio (che le clip
    // animano) tornavano a posto da soli.
    riposoClavicola: clavicola ? clavicola.quaternion.clone() : null,
    riposoPolso: polso.quaternion.clone(),
    spalla,
    gomito,
    polso,
    dita,
    punte,
    // L'OROLOGIO, che va contro-scalato (vedi scalaMano). Sono TUTTI i figli
    // diretti del polso che non sono ossa: le ossa sono le dita, e quelle
    // devono rimpicciolirsi con la mano; tutto il resto e' roba montata sul
    // polso, che sta sull'avambraccio e non cambia. Non basta cercare il
    // gruppo `Watch`: `WatchScreen` — il quad che porta tutta la UI del
    // quadrante — gli e' FRATELLO, non figlio, e cercando per nome restava
    // rimpicciolito (misurato: 0.1231 invece di 0.1578).
    // Si segna anche la POSIZIONE a riposo, non solo la scala: la scala del
    // polso moltiplica pure gli scostamenti dei figli, quindi senza
    // compensarla l'orologio resterebbe della misura giusta ma piu' vicino
    // all'osso.
    orologio: polso.children.filter((f) => !f.isBone).map((o) => ({
      oggetto: o,
      posa: o.position.clone(),
      scala: o.scale.clone(),
    })),
    // quanto le dita si stringono davvero (0..1 dei gradi tarati) e quanto le
    // punte sporgono davanti al palmo: li aggiorna tieniInMano a ogni frame
    fattoreDita: 1,
    sporgenza: 0,
    L1: gomito.getWorldPosition(new THREE.Vector3())
      .distanceTo(spalla.getWorldPosition(new THREE.Vector3())),
    L2: polso.getWorldPosition(new THREE.Vector3())
      .distanceTo(gomito.getWorldPosition(new THREE.Vector3())),
    lungoMano,
    versoPollice,
    palmo,
  }
}

/**
 * Rimpicciolisce la mano (vedi `cfgPresa.scalaMano`).
 * VA CHIAMATA A OGNI FOTOGRAMMA, DOPO IL MIXER: una clip del pacchetto anima
 * anche la scala del polso — si vede dal valore, 0.9999998 invece di 1 tondo —
 * quindi il mixer la riscrive comunque e una scalatura fatta una volta sola al
 * caricamento durerebbe un fotogramma. Ed e' per questo che sta qui e non
 * dentro tieniInMano: la mano dev'essere della sua misura anche quando non si
 * tiene niente.
 */
export function applicaScalaMano(braccio) {
  if (!braccio) return
  const s = cfgPresa.scalaMano
  braccio.polso.scale.setScalar(s)
  // l'orologio sta sull'avambraccio, che non si rimpicciolisce: resta com'era
  const so = cfgPresa.scalaOrologio / s
  for (const o of braccio.orologio) {
    o.oggetto.scale.copy(o.scala).multiplyScalar(so)
    o.oggetto.position.copy(o.posa).multiplyScalar(so)
  }
}

/**
 * Dove, DENTRO LA MANO, si appoggia il prodotto — in mondo, relativo al polso.
 * Parte dal centro del palmo e ci somma i tre scostamenti dei cursori, tutti
 * nel frame della mano: lungo le dita (`ancora`), verso il pollice
 * (`appoggioLato`), fuori dal palmo (`appoggioSopra`).
 */
function puntoAppoggio(braccio, qPolso, fuori) {
  return fuori.copy(braccio.centroPalmo).multiplyScalar(cfgPresa.ancora)
    .addScaledVector(braccio.versoPollice, cfgPresa.appoggioLato)
    .addScaledVector(braccio.palmo, cfgPresa.appoggioSopra)
    .multiplyScalar(cfgPresa.scalaMano)
    .applyQuaternion(qPolso)
}

// Ruota un osso di `qDelta` espresso in MONDO, riportando il risultato in locale
function ruotaInMondo(osso, qDelta) {
  osso.parent.getWorldQuaternion(tmp.qPar)
  osso.getWorldQuaternion(tmp.qOsso)
  osso.quaternion.copy(tmp.qPar.invert()).multiply(qDelta).multiply(tmp.qOsso)
  osso.updateMatrix()
  osso.updateWorldMatrix(false, true)
}

/**
 * Mette la mano dietro al prodotto e le da' la posa della presa.
 * Va chiamata DOPO il mixer (che riscrive le ossa a ogni fotogramma) e prima
 * del disegno.
 *
 * @param braccio  quello che torna preparaBraccio
 * @param oggetto  il prodotto in mano (Object3D)
 * @param camera   per sapere da che parte si guarda
 * @param peso     0..1, quanto la presa e' entrata (0 = braccio come sta)
 * @param delta    secondi dall'ultimo fotogramma (serve alle dita che si
 *                 adattano allo spessore dell'oggetto)
 * @returns lo SCARTO (Vector3) fra dove e' finito il centro del palmo e i piedi
 *          del prodotto: se il braccio non ci arriva e' diverso da zero, e chi
 *          chiama puo' abbassare il prodotto di altrettanto invece di lasciarlo
 *          galleggiare. Guardando in alto il prodotto va fuori portata — misurato
 *          16 cm a 30 gradi sopra l'orizzonte — e senza questo si staccava dalla
 *          mano.
 */
export function tieniInMano(braccio, oggetto, camera, peso = 1, delta = 1 / 60, congela = false) {
  if (!braccio || !oggetto || peso <= 0.001) return null
  const { spalla, gomito, polso, L1, L2 } = braccio

  // --- DOVE DEVE FINIRE LA MANO ---
  // Il prodotto si APPOGGIA sul palmo: il bersaglio non e' il centro del
  // prodotto ma la sua BASE — i piedi del modello, cioe' il centro della faccia
  // inferiore del suo ingombro — e la mano si sposta in modo che ci finisca il
  // CENTRO DEL PALMO, non il polso. `setFromObject` costa poco: gira sugli
  // ingombri gia' calcolati delle geometrie, non sui vertici.
  camera.getWorldPosition(tmp.a)
  camera.matrixWorld.extractBasis(tmp.destra, tmp.su, tmp.avanti)
  // QUANDO SI RILASCIA, LA MANO NON INSEGUE PIU'. Segnalato: «quando rilascio
  // l'oggetto il braccio va per cavoli suoi». Il prodotto, lasciato, torna al
  // suo posto sullo scaffale — che puo' essere dall'altra parte del negozio —
  // e la presa restava accesa in dissolvenza inseguendolo: per due decimi di
  // secondo il braccio puntava a un bersaglio che attraversava la stanza, si
  // stendeva al massimo e si torceva. Ora al rilascio il bersaglio si CONGELA
  // dov'era, e la dissolvenza riporta il braccio all'animazione da fermo.
  if (congela && braccio.ultimiPiedi) {
    tmp.piedi.copy(braccio.ultimiPiedi)
  } else {
    if (!scatolaInCamera(oggetto, camera, tmp.scatola)) return null
    tmp.piedi.set(
      (tmp.scatola.min.x + tmp.scatola.max.x) / 2,
      tmp.scatola.min.y,
      (tmp.scatola.min.z + tmp.scatola.max.z) / 2,
    ).applyMatrix4(camera.matrixWorld)                 // ...e via in mondo
    braccio.ultimiPiedi = (braccio.ultimiPiedi || new THREE.Vector3()).copy(tmp.piedi)
  }

  // LE DITA NON DEVONO VENIRE DAVANTI AL PRODOTTO.
  // Segnalato: «il prodotto lo vedo ancora dietro la mano». Il palmo era al suo
  // posto (misurato: 0.915 dall'occhio, dietro al prodotto) ma le dita curvate
  // SPORGONO in avanti 0.236, e la maglietta e' spessa 0.099: le punte
  // finivano a 0.67 dall'occhio contro gli 0.865 della faccia anteriore del
  // prodotto, cioe' venti centimetri davanti. Due rimedi, in quest'ordine:
  //  1. le dita si chiudono solo quanto l'oggetto e' spesso (gratis: e' anche
  //     quello che fa una mano vera, che si appiattisce dietro una cosa
  //     sottile e si curva attorno a una scatola);
  //  2. per il residuo — a dita dritte sporgono comunque 0.061 — il palmo
  //     arretra di altrettanto (costa braccio, e il correttore in
  //     ProductShowcase avvicina il prodotto di conseguenza).
  // DA CHE PARTE GUARDA IL PALMO: fra "verso l'occhio" e "in su", secondo
  // `palmoSu`. Da qui in avanti e' questa direzione a comandare tutto — di
  // quanto il palmo arretra, quanto le dita possono chiudersi, come si orienta
  // il polso — e il caso "palmo verso di noi" resta solo il valore 0.
  tmp.nMira.copy(tmp.a).sub(tmp.piedi).normalize()     // dai piedi all'occhio
    .lerp(SU, cfgPresa.palmoSu).normalize()            // ...verso l'alto
  // meta' ingombro del prodotto NELLA DIREZIONE DEL PALMO: e' lo spazio che la
  // mano ha per starci dietro senza spuntare. Col palmo verso di noi e' il
  // mezzo spessore, col palmo in su e' la mezza altezza — una formula sola.
  tmp.b.copy(tmp.nMira).transformDirection(tmp.mCam)   // la normale, in camera
  const mezzaEstensione = congela && braccio.ultimiPiedi ? braccio.ultimaEstensione : 0.5 * (
    Math.abs((tmp.scatola.max.x - tmp.scatola.min.x) * tmp.b.x) +
    Math.abs((tmp.scatola.max.y - tmp.scatola.min.y) * tmp.b.y) +
    Math.abs((tmp.scatola.max.z - tmp.scatola.min.z) * tmp.b.z)
  )
  // ...ma il limite alle dita ha senso SOLO SE SPORGERE VUOL DIRE COPRIRE.
  // Nasce dal palmo rivolto a chi guarda, dove una punta di dito davanti al
  // prodotto lo nasconde. Col palmo in su le dita salgono ATTORNO all'oggetto
  // e non coprono niente: lo reggono, ed e' quello che deve fare una mano che
  // tiene qualcosa nel palmo. Quindi il vincolo si pesa su quanto il palmo
  // guarda davvero verso l'occhio, e quando non ci guarda sparisce.
  tmp.c.copy(tmp.a).sub(tmp.piedi).normalize()         // dai piedi all'occhio
  const occlude = Math.max(0, tmp.nMira.dot(tmp.c))
  braccio.ultimaEstensione = mezzaEstensione
  const ammesso = MARGINE_DITA + mezzaEstensione + (1 - occlude) * SENZA_LIMITE
  const arretra = Math.max(0, braccio.sporgenza - ammesso)

  // dove deve finire il CENTRO DEL PALMO
  tmp.voluto.copy(tmp.piedi)
    .addScaledVector(tmp.nMira, -(cfgPresa.dietro + arretra))
    .addScaledVector(tmp.su, -cfgPresa.giu)            // ritocco: un filo piu' giu'
  // ------------------------------------------------------------
  // SI RISOLVE DUE VOLTE, E NON E' UNO SPRECO.
  // Il bersaglio del POLSO e' il punto voluto per il palmo meno lo scostamento
  // palmo-polso, che dipende da COM'E' GIRATO IL POLSO — cioe' dal risultato di
  // questo stesso calcolo. Risolvendo una volta sola quello scostamento si
  // prende dall'orientamento del fotogramma PRIMA, e l'anello si chiude con un
  // fotogramma di ritardo: un anello di retroazione ritardato oscilla, e
  // oscillava. Segnalato come «la mano va a scatti, puo' essere che va in
  // conflitto con l'animazione?» — l'animazione era innocente: MISURATO, il
  // petto, la testa, le anche, l'omero sinistro e tutto il braccio destro
  // scorrevano lisci, mentre gomito e polso facevano salti da 4.3 cm in un
  // fotogramma, a coppie, ogni 1.9 secondi. Prova del nove: azzerando lo
  // scostamento del palmo (cioe' il guadagno dell'anello) i picchi passavano da
  // 8 a ZERO e il jerk massimo da 5.45 a 0.29; rimettendolo tornavano identici.
  // Due passate chiudono l'anello DENTRO il fotogramma: la seconda usa
  // l'orientamento appena calcolato invece di quello vecchio. Costa una
  // sessantina di operazioni su quaternioni, e il blocco e' gia' idempotente
  // (la clavicola riparte dal riposo, spalla e gomito puntano sempre al
  // bersaglio, il polso si riscrive per intero).
  // ------------------------------------------------------------
  for (let passata = 0; passata < 2; passata++) {
  // il polso deve stare tanto piu' indietro quanto il palmo gli e' avanti
  polso.getWorldQuaternion(tmp.q1)
  tmp.bersaglio.copy(tmp.voluto)
    .sub(puntoAppoggio(braccio, tmp.q1, tmp.palmo3))

  // --- LA CLAVICOLA SI PORTA AVANTI ---
  // Prima del braccio: cosi' la catena a due segmenti parte da una spalla gia'
  // piu' vicina al bersaglio, e la portata utile cresce senza stirare niente.
  const clav = braccio.clavicola
  if (clav && cfgPresa.clavicola > 0) {
    // si riparte dalla posa a riposo, sempre: nessuna clip la riscrive, quindi
    // applicare la rotazione a quella del fotogramma prima la farebbe crescere
    // senza fine (vedi `riposoClavicola`)
    clav.quaternion.copy(braccio.riposoClavicola)
    clav.updateWorldMatrix(true, false)
    clav.getWorldPosition(tmp.a)
    spalla.getWorldPosition(tmp.c)
    tmp.b.copy(tmp.c).sub(tmp.a).normalize()             // com'e' messa adesso
    tmp.dir.copy(tmp.bersaglio).sub(tmp.a).normalize()   // dove punterebbe
    tmp.q2.setFromUnitVectors(tmp.b, tmp.dir)
    // solo una frazione dell'angolo, e comunque non oltre il tetto
    const angolo = 2 * Math.acos(THREE.MathUtils.clamp(Math.abs(tmp.q2.w), -1, 1))
    const tetto = THREE.MathUtils.degToRad(cfgPresa.clavicolaMax)
    const frazione = Math.min(cfgPresa.clavicola, angolo > 1e-4 ? tetto / angolo : 1) * peso
    tmp.q2.slerp(SENZA_ROTAZIONE, 1 - frazione)
    ruotaInMondo(clav, tmp.q2)
  }

  // --- CINEMATICA INVERSA A DUE SEGMENTI (legge dei coseni) ---
  spalla.updateWorldMatrix(true, false)
  spalla.getWorldPosition(tmp.a)
  tmp.dir.copy(tmp.bersaglio).sub(tmp.a)
  // fuori dall'inviluppo il braccio si ferma al suo limite: oltre la portata si
  // stende, sotto la piega minima del gomito si arresta li'. Niente strappi in
  // nessuno dei due versi, e in entrambi i casi lo scarto che si accumula dice
  // alla vetrina di spostare il prodotto.
  const cosMin = Math.cos(THREE.MathUtils.degToRad(cfgPresa.gomitoMin))
  const dMin = Math.sqrt(Math.max(0, L1 * L1 + L2 * L2 - 2 * L1 * L2 * cosMin))
  const d = THREE.MathUtils.clamp(tmp.dir.length(), dMin, (L1 + L2) * 0.999)
  tmp.dir.normalize()

  // il piano del braccio lo decide il vettore polo: il gomito va in basso e
  // indietro, come un braccio umano, non in alto
  tmp.polo.set(0, -cfgPresa.poloGiu, 0).addScaledVector(tmp.avanti, cfgPresa.poloDietro)
  tmp.n.crossVectors(tmp.dir, tmp.polo)
  if (tmp.n.lengthSq() < 1e-8) tmp.n.set(1, 0, 0)
  tmp.n.normalize()

  const cosSpalla = THREE.MathUtils.clamp((L1 * L1 + d * d - L2 * L2) / (2 * L1 * d), -1, 1)
  const angolo = Math.acos(cosSpalla)

  // direzione voluta per l'omero: la linea spalla-bersaglio ruotata di `angolo`
  tmp.b.copy(tmp.dir).applyQuaternion(tmp.q1.setFromAxisAngle(tmp.n, angolo))
  // direzione attuale dell'omero
  gomito.getWorldPosition(tmp.c)
  tmp.c.sub(tmp.a).normalize()
  tmp.q2.setFromUnitVectors(tmp.c, tmp.b)
  if (peso < 1) tmp.q2.slerp(SENZA_ROTAZIONE, 1 - peso)
  ruotaInMondo(spalla, tmp.q2)

  // --- avambraccio: punta al bersaglio ---
  gomito.getWorldPosition(tmp.a)
  polso.getWorldPosition(tmp.c)
  tmp.b.copy(tmp.bersaglio).sub(tmp.a).normalize()
  tmp.c.sub(tmp.a).normalize()
  tmp.q2.setFromUnitVectors(tmp.c, tmp.b)
  if (peso < 1) tmp.q2.slerp(SENZA_ROTAZIONE, 1 - peso)
  ruotaInMondo(gomito, tmp.q2)

  // --- POLSO: il palmo guarda dove dice `nMira`, le dita proseguono il braccio ---
  // Si costruisce la terna voluta in mondo e la si confronta con quella della
  // mano a riposo: il quaternione che porta l'una sull'altra e' l'orientamento.
  polso.getWorldPosition(tmp.a)
  tmp.n.copy(tmp.nMira)                                                  // normale del palmo
  // LE DITA PROSEGUONO L'AVAMBRACCIO, non puntano "in su".
  // Con le dita verso l'alto il polso risultava piegato a 91 gradi rispetto
  // all'avambraccio (misurato): un angolo che nessun polso fa, e si vedeva.
  // Nella foto di riferimento l'avambraccio sale e la mano lo prosegue quasi
  // dritta. Quindi la direzione delle dita e' quella dell'avambraccio,
  // proiettata perpendicolare al palmo, piu' una piega di pochi gradi.
  gomito.getWorldPosition(tmp.c)
  tmp.b.copy(tmp.a).sub(tmp.c).projectOnPlane(tmp.n).normalize()
  tmp.dir.crossVectors(tmp.b, tmp.n)                                     // verso il pollice
  tmp.dir.normalize()
  // LA PIEGA RUOTA TUTTA LA TERNA attorno all'asse del pollice, non il solo
  // asse delle dita: ruotando solo quello la terna smette di essere ortogonale
  // e setFromRotationMatrix, che presuppone una rotazione pura, restituisce un
  // quaternione sbagliato. MISURATO: il palmo finiva a 0.14 dall'occhio invece
  // che a 1. Piegando il polso il palmo si inclina un poco, ed e' giusto cosi':
  // e' quello che fa un polso vero.
  if (cfgPresa.piegaPolso) {
    tmp.q1.setFromAxisAngle(tmp.dir, THREE.MathUtils.degToRad(cfgPresa.piegaPolso))
    tmp.b.applyQuaternion(tmp.q1).normalize()
    tmp.n.applyQuaternion(tmp.q1).normalize()
  }
  tmp.m1.makeBasis(braccio.versoPollice, braccio.lungoMano, braccio.palmo)
  tmp.m2.makeBasis(tmp.dir, tmp.b, tmp.n)
  tmp.q1.setFromRotationMatrix(tmp.m2).multiply(tmp.q2.setFromRotationMatrix(tmp.m1).invert())
  // LA TORSIONE LA PORTA L'AVAMBRACCIO, NON IL POLSO. Segnalato: «l'orologio
  // non segue la direzione del braccio». L'orologio e' figlio dell'osso del
  // POLSO (verificato: la sua trasformata relativa al polso e' costante al
  // quarto decimale, quindi e' agganciato bene), quindi girava insieme alla
  // mano — e la mano, per mettere il palmo in faccia a chi guarda, doveva
  // torcersi di parecchio rispetto all'avambraccio. In un braccio vero quella
  // torsione la fa il radio sull'ulna, cioe' l'AVAMBRACCIO, e l'orologio gira
  // con lui. Quindi la rotazione che serve al polso si scompone in torsione
  // attorno all'asse del braccio + piegamento: la prima va al gomito, il resto
  // resta al polso.
  polso.getWorldQuaternion(tmp.qOsso)
  tmp.q2.copy(tmp.q1).multiply(tmp.qOsso.invert())        // rotazione mancante, in mondo
  // IL SEGNO VA NORMALIZZATO PRIMA DI SCOMPORRE. Un quaternione e il suo
  // opposto sono la STESSA rotazione, ma danno due torsioni diverse: una corta
  // e una che gira dall'altra parte, e insieme fanno un giro intero. Se il
  // quaternione capita col segno "lungo", la scomposizione tira fuori la
  // torsione sbagliata e l'avambraccio si avvita — segnalato («sembra che
  // l'avambraccio sia girato di 360 facendo un nodo al gomito»). Bastano
  // quattro cambi di segno per non doverci piu' pensare.
  if (tmp.q2.w < 0) { tmp.q2.x = -tmp.q2.x; tmp.q2.y = -tmp.q2.y; tmp.q2.z = -tmp.q2.z; tmp.q2.w = -tmp.q2.w }
  gomito.getWorldPosition(tmp.a)
  polso.getWorldPosition(tmp.c)
  tmp.dir.copy(tmp.c).sub(tmp.a).normalize()              // asse dell'avambraccio
  // torsione = parte della rotazione attorno a quell'asse (swing-twist)
  const proiezione = tmp.q2.x * tmp.dir.x + tmp.q2.y * tmp.dir.y + tmp.q2.z * tmp.dir.z
  tmp.qTors.set(tmp.dir.x * proiezione, tmp.dir.y * proiezione, tmp.dir.z * proiezione, tmp.q2.w)
  if (tmp.qTors.lengthSq() > 1e-8) {
    tmp.qTors.normalize()
    // ...E NON TUTTA AL GOMITO. Il rig ha UN SOLO osso per l'avambraccio,
    // quindi tutta la torsione che gli si da' viene applicata dall'inizio
    // dell'osso: la pelle vicino al gomito e' pesata a meta' sull'omero, che
    // non gira, e a meta' sull'avambraccio, che gira tutto — e in quei due
    // centimetri si strizza. Un avambraccio vero fa il contrario: il radio
    // ruota sull'ulna, quindi la torsione e' quasi nulla al gomito e massima
    // al polso. Senza un osso di torsione in mezzo, il modo di avvicinarsi e'
    // dividerla fra avambraccio e polso. MISURATA prima: 70-108 gradi tutti
    // sul gomito.
    const gradi = 2 * Math.acos(THREE.MathUtils.clamp(tmp.qTors.w, -1, 1))
    const tetto = THREE.MathUtils.degToRad(cfgPresa.torsioneMax)
    let quota = cfgPresa.torsioneAvambraccio
    if (gradi * quota > tetto) quota = tetto / gradi
    // quello che l'avambraccio non prende resta al polso da se': subito dopo
    // il polso viene riscritto con l'orientamento voluto, qualunque cosa abbia
    // fatto il gomito
    if (quota < 1) tmp.qTors.slerp(SENZA_ROTAZIONE, 1 - quota)
    if (peso < 1) tmp.qTors.slerp(SENZA_ROTAZIONE, 1 - peso)
    ruotaInMondo(gomito, tmp.qTors)
  }
  // al polso resta il solo piegamento: si riscrive l'orientamento voluto, che
  // ora dista dall'avambraccio molto meno
  polso.parent.getWorldQuaternion(tmp.qPar)
  tmp.qOsso.copy(tmp.qPar).invert().multiply(tmp.q1)
  // dal RIPOSO verso la posa voluta, non dal valore del fotogramma prima:
  // nessuna clip anima il polso, quindi partendo da dov'era la presa, quando
  // si spegne, lo lascerebbe dov'e' invece di riportarlo all'animazione
  if (peso < 1) polso.quaternion.copy(braccio.riposoPolso).slerp(tmp.qOsso, peso)
  else polso.quaternion.copy(tmp.qOsso)
  polso.updateMatrix()
  polso.updateWorldMatrix(false, false)   // la passata dopo rilegge da qui
  }

  // --- DITA: si curvano attorno all'asse perpendicolare al dito e al palmo ---
  const chiusura = braccio.fattoreDita * peso
  for (const d2 of braccio.dita) {
    const pollice = d2.nome === 'Thumb'
    const gradi = pollice
      ? (d2.grado === 1 ? cfgPresa.pollice1 : cfgPresa.pollice2)
      : (d2.grado === 1 ? cfgPresa.dita1 : d2.grado === 2 ? cfgPresa.dita2 : cfgPresa.dita3)
    // l'asse di piega e' nel frame dell'osso: il dito si chiude verso il palmo
    tmp.dir.copy(d2.lungo).cross(braccio.palmo).normalize()
    // sempre a partire dal riposo: assoluto, non cumulativo
    d2.osso.quaternion.copy(d2.riposo).multiply(
      tmp.q1.setFromAxisAngle(tmp.dir, THREE.MathUtils.degToRad(gradi) * chiusura)
    )
    d2.osso.updateMatrix()
  }
  polso.updateWorldMatrix(false, true)

  // --- QUANTO LA MANO SPORGE, E DI CONSEGUENZA QUANTO STRINGERE ---
  // Si misura sulle ossa vere invece di calcolarla: cosi' vale per qualunque
  // posa di riposo e qualunque taratura dei cursori, senza modelli da tenere
  // aggiornati. La si usa al fotogramma DOPO (per l'arretramento e per il
  // fattore di chiusura): un fotogramma di ritardo su un movimento che dura
  // decimi di secondo non si vede.
  polso.getWorldPosition(tmp.a)
  polso.getWorldQuaternion(tmp.q1)
  puntoAppoggio(braccio, tmp.q1, tmp.palmo3)
  tmp.a.add(tmp.palmo3)                                  // centro del palmo, in mondo
  tmp.n.copy(braccio.palmo).applyQuaternion(tmp.q1)      // normale del palmo
  let sporgenza = 0
  for (const punta of braccio.punte) {
    const d3 = punta.getWorldPosition(tmp.c).sub(tmp.a).dot(tmp.n)
    if (d3 > sporgenza) sporgenza = d3
  }
  braccio.sporgenza = sporgenza
  // controllo integrale: le dita si stringono finche' sporgono meno di quanto
  // l'oggetto e' spesso, e si riaprono appena c'e' spazio. Niente formule
  // inverse da invertire, e si assesta in un paio di decimi di secondo.
  braccio.fattoreDita = THREE.MathUtils.clamp(
    braccio.fattoreDita + (ammesso - sporgenza) * cfgPresa.adattaDita * delta, 0, 1,
  )

  // Quanto il palmo e' rimasto lontano da DOVE DOVEVA ANDARE: e' zero quando il
  // braccio ci arriva, e cresce quando il bersaglio esce dalla portata. Si
  // misura sul bersaglio vero e non sui piedi del prodotto, altrimenti gli
  // scostamenti VOLUTI (`dietro`, `giu`, l'arretramento qui sopra) verrebbero
  // letti come "la mano non ci arriva" e il correttore in ProductShowcase si
  // metterebbe a spingere il prodotto per compensarli. E' per questo che
  // `dietro` era stato azzerato; ora torna usabile.
  return tmp.scarto.copy(tmp.a).sub(tmp.voluto)
}
